import { Sun, Eclipse, Moon } from 'lucide-react'
import { useTheme } from './ThemeProvider'
import type { ThemeMode } from './useSettings'

// Click order matches the icon progression the design calls for:
//   light (sun) → dim (eclipse) → dark (moon) → …
const NEXT: Record<ThemeMode, ThemeMode> = { light: 'dim', dim: 'dark', dark: 'light' }
const ICON: Record<ThemeMode, typeof Sun> = { light: Sun, dim: Eclipse, dark: Moon }
const LABEL: Record<ThemeMode, string> = { light: 'Light', dim: 'Dim', dark: 'Dark' }

/**
 * Single-button dark/dim/light toggle for a shell's top bar. The button is a
 * round, theme-tinted swatch showing the current mode (full circle / half
 * circle / moon); clicking cycles to the next. Flips the given `area`'s theme
 * (default `workspace`); when `syncAll` is on it updates every area at once —
 * the same rule as the Settings → Theme page.
 */
export function ThemeToggle({ area = 'workspace' }: { area?: 'drive' | 'workspace' | 'chat' }) {
  const { settings, updateSetting } = useTheme()
  const active: ThemeMode = settings.theme.syncAll ? settings.theme.workspace : settings.theme[area]

  const cycle = () => {
    const next = NEXT[active]
    if (settings.theme.syncAll) {
      updateSetting('theme', { ...settings.theme, drive: next, workspace: next, chat: next })
    } else {
      updateSetting('theme', { ...settings.theme, [area]: next })
    }
  }

  const Icon = ICON[active]
  return (
    <button
      onClick={cycle}
      title={`Theme: ${LABEL[active]} — click to switch`}
      aria-label={`Theme: ${LABEL[active]}. Click to switch theme.`}
      className="flex items-center justify-center w-6 h-6 rounded-full themed-bg-tertiary themed-text-secondary hover:themed-text transition-colors"
    >
      <Icon size={14} />
    </button>
  )
}
